'use client';

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '@/hooks/useToast';
import { usePublishArticle } from '@/hooks/usePublishArticle';
import { articlesApi } from '@/app/api/articles';
import { TagIcon, CalendarIcon, UserIcon, FolderIcon } from '@heroicons/react/24/outline';
import { MarkdownRenderer } from '@/components/markdown/MarkdownRenderer';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { apiConfig } from '@/config/api';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';

import ArticlePublishWizard from '../components/ArticlePublishWizard';

export default function ArticlePreviewPage() {
  const { user } = useAuth();

  return (

    <ArticlePreview />

  );
}

function ArticlePreview() {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [author, setAuthor] = useState('');
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [lastModified, setLastModified] = useState<Date | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState('');
  const [slug, setSlug] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const navigate = useNavigate();
  const { addToast } = useToast();
  const location = useLocation();

  // Get document ID from URL
  const [searchParams] = useSearchParams();
  const documentId = searchParams ? searchParams.get('id') : null;

  // Use publish hook for unified publish logic
  // Use publish hook for unified publish logic
  const {
    isWizardOpen,
    isPublishing,
    wizardInitialMeta,
    articleContent: wizardArticleContent,
    initiatePublish,
    handleWizardPublish,
    closeWizard
  } = usePublishArticle({
    onSuccess: () => {
      addToast('Article published successfully!', 'success');
      setStatus('published');
    },
    onError: (error) => {
      addToast('Failed to publish article. Please try again.', 'error');
    }
  });

  // Handle state change actions
  const handlePublish = async () => {
    if (!documentId) return;
    await initiatePublish(documentId);
  };

  const handleArchive = async () => {
    if (!documentId) return;

    if (confirm('Are you sure you want to archive this article?')) {
      setActionLoading(true);
      try {
        await articlesApi.updateArticle(documentId, { state: 'archived' });
        setStatus('archived');
        alert('Article archived successfully!');
        setActionLoading(false);
      } catch (error) {
        console.error('Error archiving article:', error);
        alert('Failed to archive article. Please try again.');
        setActionLoading(false);
      }
    }
  };

  const handleClone = async () => {
    if (!documentId) return;

    if (confirm('Are you sure you want to clone this article? A new draft will be created.')) {
      setActionLoading(true);
      try {
        const clonedArticle = await articlesApi.cloneArticle(documentId);
        alert('Article cloned successfully!');
        setActionLoading(false);
        navigate(`/articles/edit?id=${clonedArticle.uuid}`);
      } catch (error) {
        console.error('Error cloning article:', error);
        alert('Failed to clone article. Please try again.');
        setActionLoading(false);
      }
    }
  };

  const handleRestore = async () => {
    if (!documentId) return;

    if (confirm('Are you sure you want to restore this article to draft?')) {
      setActionLoading(true);
      try {
        await articlesApi.updateArticle(documentId, { state: 'draft' });
        setStatus('draft');
        alert('Article restored successfully!');
        setActionLoading(false);
      } catch (error) {
        console.error('Error restoring article:', error);
        alert('Failed to restore article. Please try again.');
        setActionLoading(false);
      }
    }
  };

  // Handle warnings passed via navigation state (e.g., from Edit page redirect)
  useEffect(() => {
    if (location.state?.warning) {
      addToast(location.state.warning, 'error');
      // Clear state to avoid showing it again if user navigates back/forward
      navigate(location.pathname + location.search, { replace: true, state: {} });
    }
  }, [location.state, addToast, navigate, location.pathname, location.search]);

  // Fetch document data
  useEffect(() => {
    const fetchDocumentData = async () => {
      if (!documentId) {
        setError('No document ID provided');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        // Fetch document metadata
        const document = await articlesApi.getArticle(documentId);

        // Fetch document content
        const contentBlob = await articlesApi.getArticleContent(documentId);
        const contentText = await contentBlob.text();

        // Fetch document tags
        const documentTags = await articlesApi.getArticleTags(documentId);

        // Set state
        setTitle(document.meta?.title || '');
        setContent(contentText);
        setAuthor(document.meta?.author || '');
        setCategory(document.meta?.category || '');
        setTags(documentTags || []);
        setSlug(document.slug || '');
        setLastModified(document.updatedAt ? new Date(Number(document.updatedAt) * 1000) : null);
        const documentStatus = document.state || 'draft';
        setStatus(documentStatus);

        // Redirect if published
        if (documentStatus === 'published') {
          const publicUrl = `${apiConfig.wwwUrl}/articles/${document.slug || documentId}`;
          window.location.href = publicUrl;
          return;
        }
      } catch (error) {
        console.error('Error fetching document:', error);
        setError('Failed to load document. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchDocumentData();
  }, [documentId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
        <p className="ml-2">Loading article...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <div className="bg-red-50 border-l-4 border-red-400 p-4 w-full max-w-2xl">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
        <Link to="/articles" className="mt-4 text-blue-600 hover:text-blue-800">
          Return to Articles
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen py-8">
      <div className="max-w-4xl mx-auto bg-white shadow-md rounded-lg overflow-hidden">
        {/* Article Header */}
        <div className="p-6 border-b border-gray-200">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">{title}</h1>

          <div className="flex flex-wrap items-center text-sm text-gray-500 gap-4">
            {author && (
              <div className="flex items-center">
                <UserIcon className="h-4 w-4 mr-1" />
                <span>{author}</span>
              </div>
            )}

            {lastModified && (
              <div className="flex items-center">
                <CalendarIcon className="h-4 w-4 mr-1" />
                <span>
                  {lastModified.toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
            )}

            {category && (
              <div className="flex items-center">
                <FolderIcon className="h-4 w-4 mr-1" />
                <span>{category}</span>
              </div>
            )}
          </div>

          {tags && tags.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {tags.map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                >
                  <TagIcon className="h-3 w-3 mr-1" />
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Article Content */}
        <div className="p-6">
          <MarkdownRenderer content={content} className="prose-lg" justified={true} />
        </div>

        {/* Article Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-between items-center">
            <Link to="/articles" className="text-blue-600 hover:text-blue-800">
              ← Back to Articles
            </Link>
            <div className="flex space-x-2">
              {/* State change buttons based on current status */}
              {status === 'draft' && (
                <button
                  onClick={handlePublish}
                  disabled={actionLoading}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  {actionLoading ? (
                    <>
                      <div className="inline-block animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                      Publishing...
                    </>
                  ) : (
                    <>Publish</>
                  )}
                </button>
              )}

              {status === 'published' && (
                <>
                  <a
                    href={`${apiConfig.wwwUrl}/articles/${slug || documentId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-1.5" />
                    View Live
                  </a>
                  <button
                    onClick={handleArchive}
                    disabled={actionLoading}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                  >
                    {actionLoading ? (
                      <>
                        <div className="inline-block animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                        Archiving...
                      </>
                    ) : (
                      <>Archive</>
                    )}
                  </button>
                </>
              )}

              {status === 'archived' && (
                <button
                  onClick={handleRestore}
                  disabled={actionLoading}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {actionLoading ? (
                    <>
                      <div className="inline-block animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                      Restoring...
                    </>
                  ) : (
                    <>Restore to Draft</>
                  )}
                </button>
              )}

              <button
                onClick={() => window.print()}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Print
              </button>

              {(status === 'draft' || status === 'published') && (
                <button
                  onClick={handleClone}
                  disabled={actionLoading}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  {actionLoading ? (
                    <>
                      <div className="inline-block animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-gray-900 mr-2"></div>
                      Cloning...
                    </>
                  ) : (
                    <>
                      Clone
                    </>
                  )}
                </button>
              )}

              {status === 'draft' && (
                <Link
                  to={`/articles/edit?id=${documentId}`}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Edit Article
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Slug Publish Modal */}
      {/* Article Publish Wizard */}
      <ArticlePublishWizard
        isOpen={isWizardOpen}
        onClose={closeWizard}
        onPublish={handleWizardPublish}
        articleId={documentId || ''}
        articleTitle={title}
        initialMeta={wizardInitialMeta}
        articleContent={wizardArticleContent}
        availableTags={[]} // Suggestions not loaded in preview
        availableCategories={[]} // Suggestions not loaded in preview
        isPublishing={isPublishing}
      />
    </div>
  );
}
